import { useInfiniteQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { fetchResources } from 'api/fetchers/marketing-capabilities/resources/fetchResources'
import { alphaSortOptions, SortValue } from 'pages/exploreLearnings/constants/sortOptions.constants'
import { useExploreLearnings } from 'store/ExploreLearnings'

import { usePractitionersOus } from '../practitioners/usePractitionersOUs'
import { useSkills } from '../skills/useSkills'

const pageResolver = ({ data }: any) => {
  return data.meta.page !== data.meta.totalPages ? data.meta.page + 1 : undefined
}

export const useResources = () => {
  const [searchParams] = useSearchParams()
  const search = useExploreLearnings(state => state.search)
  const onboarding = useExploreLearnings(state => state.onboarding)
  const ouId = useExploreLearnings(state => state.OU)
  const skills = useExploreLearnings(state => state.skill)
  const sort = useExploreLearnings(state => state.sort)

  const urlOnboarding = searchParams.get('onboarding')
  const urlSkillsParam = searchParams.get('skills')

  const { data: skillList } = useSkills(true)

  const validResourceSorts: string[] = alphaSortOptions.map(o => o.value)
  let effectiveSort = sort
  if (!validResourceSorts.includes(sort)) {
    effectiveSort = SortValue.AZ
    useExploreLearnings.setState({ sort: effectiveSort })
  }

  const { data: ouList } = usePractitionersOus()
  const validOuIds = ouList ? ouList.map((ou: any) => ou.id) : []
  const effectiveOus = ouId.filter(id => validOuIds.includes(id))

  // Handle URL skills param - map names to IDs
  // Only use store skills if no URL param is present (URL params take precedence)
  let effectiveSkills = urlSkillsParam ? [] : skills
  if (urlSkillsParam && skillList) {
    const skillLabels = urlSkillsParam.split(',').map(s => s.trim())
    const skillListData = Array.isArray(skillList) ? skillList : skillList?.data
    const validSkills = skillLabels
      .map(label => skillListData.find((skill: any) => skill.name === label))
      .filter((skill: any): skill is any => !!skill)
    effectiveSkills = validSkills.map((skill: any) => skill.id)
  }

  // Only use URL onboarding param if present, otherwise use store state
  const effectiveOnboarding = urlOnboarding !== null ? urlOnboarding : onboarding

  const params = {
    ...(search !== '' && { searchTerm: search }),
    ...(effectiveOnboarding === 'true' && { onboarding: true }),
    pageSize: 9,
    ...(effectiveOus.length > 0 && { ouId: effectiveOus.join(',') }),
    ...(effectiveSkills.length > 0 && { skillId: effectiveSkills.join(',') }),
    sortBy: effectiveSort,
  }

  const queryFn = ({ pageParam = 1, signal }: any) => fetchResources({ ...params, page: pageParam })(signal)

  return useInfiniteQuery({
    queryKey: ['resources', params],
    queryFn,
    initialPageParam: 1,
    getNextPageParam: pageResolver,
    enabled: !urlSkillsParam || !!skillList,
    select: ({ pages, pageParams }) => ({
      pages: pages.map((page: any) => page.data.data),
      pageParams,
      exploreMoreResources: pages[0]?.data?.exploreMoreResources,
      meta: pages[0]?.data?.meta,
    }),
  })
}
